import { Injectable, Injector } from '@angular/core';

import { SnCurrentOrganization } from '@lib/auth';

import { IFsLprProviderInterface } from '../../api/interfaces';
import {
  AxonVIProviderService,
  FlockProviderService,
  FlockV3ProviderService,
  FususProviderService,
  VigilantProviderService,
} from '../../api/providers';
import { AxonVILprService } from '../../integrations';
import { ViLPRProvider } from '../../shared';
import { ViLPRProviderIntegrationService } from '../../shared/services/lpr-provider-integration/lpr-provider-integration.service';

/**
 * Creates and manages LPR data source providers based on enabled integrations
 * Used by SDM to get all available sources for the source selection dropdown
 */
@Injectable({ providedIn: 'root' })
export class LPRSourceManagerService {
  constructor(
    private readonly lprIntegrationsService: ViLPRProviderIntegrationService,
    private readonly injector: Injector,
    private readonly flockProviderService: FlockProviderService,
    private readonly flockV3ProviderService: FlockV3ProviderService,
    private readonly vigilantProviderService: VigilantProviderService,
    private readonly fususProviderService: FususProviderService,
  ) {}

  /**
   * Returns all providers the organization has access to
   * Axon VI takes priority over Fleet. Flock takes priority over Flock V3
   */
  public getAvailableProviders(): IFsLprProviderInterface[] {
    const providers: IFsLprProviderInterface[] = [];

    if (this.lprIntegrationsService.isVehicleIntelligenceEnabled) {
      const axonVILprService = this.injector.get(AxonVILprService);
      const currentOrg = this.injector.get(SnCurrentOrganization);
      /**
       * NOTE: When this service is used, it means we're in SDM mode (sourceAndDeviceManagementEnabled = true).
       * In SDM mode, the `name` parameter passed to AxonVIProviderService constructor is not used because
       * the provider's `nameEnum` property is set to ViLPRProvider.AXON_VI and the display name comes from
       * the provider's translation key, not from the constructor parameter. The name parameter is only
       * relevant for non-SDM mode where separate provider instances are created for Outpost, Lightpost, etc.
       */
      const axonVIProvider = new AxonVIProviderService(
        axonVILprService,
        ViLPRProvider.AXON_VI,
        currentOrg,
      );
      providers.push(axonVIProvider);
    }

    if (this.lprIntegrationsService.isVigilantEnabled) {
      providers.push(this.vigilantProviderService);
    }

    if (this.lprIntegrationsService.isFlockEnabled) {
      providers.push(this.flockProviderService);
    } else if (this.lprIntegrationsService.isFlockV3Enabled) {
      providers.push(this.flockV3ProviderService);
    }

    if (this.lprIntegrationsService.isFususEnabled) {
      providers.push(this.fususProviderService);
    }

    return providers;
  }
}
